import React, { useEffect, useState } from "react";
import { Routes, Route, NavLink, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Wordmark } from "@/components/Brand";
import Dashboard from "@/pages/admin/Dashboard";
import Sessions from "@/pages/admin/Sessions";
import SessionDetail from "@/pages/admin/SessionDetail";
import Users from "@/pages/admin/Users";
import Audit from "@/pages/admin/Audit";
import Variance from "@/pages/admin/Variance";
import Settings from "@/pages/admin/Settings";
import Security from "@/pages/admin/Security";
import EmailLog from "@/pages/admin/EmailLog";
import AccessTrail from "@/pages/admin/AccessTrail";
import MandatoryMFA from "@/pages/admin/MandatoryMFA";
import { LayoutDashboard, FileStack, Users as UsersIcon, ScrollText, TriangleAlert, Settings as Cog, ShieldCheck, Mail, KeyRound, LogOut, Menu, X } from "lucide-react";

export default function AdminApp() {
  const { user, logout, refresh } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  if (user.mfa_setup_required) return <MandatoryMFA onDone={refresh} onLogout={() => { logout(); nav("/"); }} />;

  const isAdmin = user.role === "admin" || user.role === "superadmin";
  const isAuditor = user.role === "auditor";
  const isCoord = user.role === "coordinator";

  const links = [
    { to: "/admin", label: "Dashboard", icon: LayoutDashboard, show: isAdmin || isAuditor },
    { to: "/admin/sessions", label: "Sessions", icon: FileStack, show: true },
    { to: "/admin/variance", label: "Variance & Deposits", icon: TriangleAlert, show: isAdmin || isAuditor },
    { to: "/admin/users", label: "Roster", icon: UsersIcon, show: isAdmin || isCoord },
    { to: "/admin/audit", label: "Audit Log", icon: ScrollText, show: isAdmin || isAuditor },
    { to: "/admin/security", label: "Security", icon: ShieldCheck, show: isAdmin || isAuditor },
    { to: "/admin/access", label: "Access Trail", icon: KeyRound, show: isAdmin || isAuditor },
    { to: "/admin/emails", label: "Email Log", icon: Mail, show: isAdmin || isAuditor },
    { to: "/admin/settings", label: "Settings", icon: Cog, show: isAdmin },
  ].filter((l) => l.show);

  return (
    <div className="min-h-screen flex">
      {/* Sidebar */}
      <aside className={`fixed lg:static z-40 inset-y-0 left-0 w-72 bg-white border-r border-[color:var(--line)] p-5 flex flex-col transition-transform ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="flex items-center justify-between mb-8">
          <Wordmark />
          <button className="lg:hidden btn btn-ghost h-9 w-9 p-0" onClick={() => setOpen(false)}><X size={18} /></button>
        </div>
        <nav className="space-y-1 flex-1">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.to === "/admin"} onClick={() => setOpen(false)}
              className={({ isActive }) => `flex items-center gap-3 px-4 h-12 rounded-lg font-medium ${isActive ? "bg-[color:var(--olive)] text-white" : "text-[color:var(--ink-2)] active:bg-[color:var(--sage-light)]"}`}
              data-testid={`nav-${l.label.split(" ")[0].toLowerCase()}`}>
              <l.icon size={18} /> {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-[color:var(--line)] pt-4">
          <div className="px-2 mb-3">
            <div className="font-semibold text-sm">{user.name}</div>
            <div className="text-xs text-[color:var(--muted)] capitalize">{user.role}</div>
          </div>
          <button className="btn btn-ghost w-full h-11" onClick={() => { logout(); nav("/"); }} data-testid="admin-logout"><LogOut size={16} /> Sign Out</button>
        </div>
      </aside>
      {open && <div className="fixed inset-0 bg-black/20 z-30 lg:hidden" onClick={() => setOpen(false)} />}

      {/* Content */}
      <div className="flex-1 min-w-0">
        <header className="lg:hidden flex items-center gap-3 p-4 border-b border-[color:var(--line)] bg-white">
          <button className="btn btn-ghost h-10 w-10 p-0" onClick={() => setOpen(true)}><Menu size={20} /></button>
          <Wordmark sub={false} />
        </header>
        <main className="p-5 sm:p-8 max-w-6xl mx-auto fadeup">
          <Routes>
            <Route index element={isCoord ? <Navigate to="sessions" replace /> : <Dashboard />} />
            <Route path="sessions" element={<Sessions />} />
            <Route path="sessions/:id" element={<SessionDetail />} />
            <Route path="variance" element={<Variance />} />
            <Route path="users" element={<Users />} />
            <Route path="audit" element={<Audit />} />
            <Route path="security" element={<Security />} />
            <Route path="access" element={<AccessTrail />} />
            <Route path="emails" element={<EmailLog />} />
            <Route path="settings" element={<Settings />} />
            <Route path="*" element={<Navigate to="/admin" replace />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}
